import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  GlobalSignOutCommand,
  AdminCreateUserCommand,
  AdminSetUserPasswordCommand,
  ChangePasswordCommand,
  GetUserCommand,
  UpdateUserAttributesCommand,
  RespondToAuthChallengeCommand,
  ResendConfirmationCodeCommand,
  AttributeType,
  AuthFlowType,
  ChallengeNameType
} from "@aws-sdk/client-cognito-identity-provider";

// AWS Configuration
const region = process.env.REACT_APP_AWS_REGION;
const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
const clientId = process.env.REACT_APP_COGNITO_APP_CLIENT_ID;

// Initialize Cognito client. Every call used here is authorised by the user's
// password or tokens, so the browser needs no AWS credentials.
const cognitoClient = new CognitoIdentityProviderClient({ region });

// Helper function to sanitize phone numbers to +44 format
export const sanitizePhoneNumber = (phoneNumber: string): string => {
  // Remove all non-numeric characters
  const numericOnly = phoneNumber.replace(/\D/g, '');
  
  // If the number starts with 0, replace it with +44
  if (numericOnly.startsWith('0')) {
    return `+44${numericOnly.substring(1)}`;
  }
  
  // If the number already starts with 44, add a +
  if (numericOnly.startsWith('44')) {
    return `+${numericOnly}`;
  }
  
  // Otherwise, assume it's already in the correct format
  return `+44${numericOnly}`;
};

// Helper function to sanitize device ID (alphanumeric and capitalized)
export const sanitizeDeviceId = (deviceId: string): string => {
  // Remove non-alphanumeric characters and convert to uppercase
  return deviceId.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
};

// Chameleon device IDs are 16 hex characters, e.g. 0CA2F400007ADE3A
export const isValidDeviceId = (deviceId: string): boolean => /^[0-9A-F]{16}$/.test(deviceId);

// preferred_username is required by the pool, so accounts without a device get a
// unique placeholder. Anything that isn't a valid device ID counts as "no device".
const pendingDevicePlaceholder = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(4));
  return `PENDING-${Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
};

// User login
export const loginUser = async (username: string, password: string) => {
  const params = {
    AuthFlow: "USER_PASSWORD_AUTH" as AuthFlowType,
    ClientId: clientId,
    AuthParameters: {
      USERNAME: username,
      PASSWORD: password
    }
  };

  try {
    const command = new InitiateAuthCommand(params);
    const response = await cognitoClient.send(command);
    return response;
  } catch (error) {
    console.error("Error during login:", error);
    throw error;
  }
};

// User registration
export const registerUser = async (
  username: string, 
  password: string, 
  email: string, 
  phoneNumber: string,
  fullName: string,
  deviceId?: string
) => {
  // Sanitize inputs
  const sanitizedPhone = sanitizePhoneNumber(phoneNumber);
  const sanitizedDeviceId = sanitizeDeviceId(deviceId || '');
  if (sanitizedDeviceId && !isValidDeviceId(sanitizedDeviceId)) {
    throw new Error('Device ID must be 16 characters: digits 0-9 and letters A-F.');
  }

  const params = {
    ClientId: clientId,
    Username: username,
    Password: password,
    UserAttributes: [
      {
        Name: "email",
        Value: email
      },
      {
        Name: "phone_number",
        Value: sanitizedPhone
      },
      {
        Name: "name",
        Value: fullName
      },
      {
        Name: "preferred_username",
        Value: sanitizedDeviceId || pendingDevicePlaceholder()
      }
    ]
  };

  try {
    const command = new SignUpCommand(params);
    const response = await cognitoClient.send(command);
    return response;
  } catch (error) {
    console.error("Error during registration:", error);
    throw error;
  }
};

// Confirm user sign up with verification code
export const confirmSignUp = async (username: string, code: string) => {
  const params = {
    ClientId: clientId,
    Username: username,
    ConfirmationCode: code,
  };

  try {
    const command = new ConfirmSignUpCommand(params);
    await cognitoClient.send(command);
    return true;
  } catch (error) {
    console.error("Error confirming sign up:", error);
    throw error;
  }
};

// Resend verification code
export const resendSignUp = async (username: string) => {
  const params = {
    ClientId: clientId,
    Username: username,
  };

  try {
    const command = new ResendConfirmationCodeCommand(params);
    await cognitoClient.send(command);
    return true;
  } catch (error) {
    console.error("Error resending verification code:", error);
    throw error;
  }
};

// Swap the stored refresh token for a new access token; null if the session can't be renewed
export const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  try {
    const response = await cognitoClient.send(new InitiateAuthCommand({
      AuthFlow: "REFRESH_TOKEN_AUTH" as AuthFlowType,
      ClientId: clientId,
      AuthParameters: { REFRESH_TOKEN: refreshToken }
    }));
    const accessToken = response.AuthenticationResult?.AccessToken;
    if (!accessToken) return null;
    localStorage.setItem('accessToken', accessToken);
    return accessToken;
  } catch (error) {
    console.error("Error refreshing session:", error);
    return null;
  }
};

// Get current user
export const getCurrentUser = async (accessToken: string) => {
  const params = {
    AccessToken: accessToken
  };

  try {
    const command = new GetUserCommand(params);
    const response = await cognitoClient.send(command);
    return response;
  } catch (error) {
    console.error("Error getting current user:", error);
    throw error;
  }
};

// Change password
export const changePassword = async (
  accessToken: string,
  oldPassword: string,
  newPassword: string
) => {
  const params = {
    AccessToken: accessToken,
    PreviousPassword: oldPassword,
    ProposedPassword: newPassword
  };

  try {
    const command = new ChangePasswordCommand(params);
    const response = await cognitoClient.send(command);
    return response;
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};

// Sign out
export const signOut = async (accessToken: string) => {
  const params = {
    AccessToken: accessToken
  };

  try {
    const command = new GlobalSignOutCommand(params);
    const response = await cognitoClient.send(command);
    return response;
  } catch (error) {
    console.error("Error during sign out:", error);
    throw error;
  }
};

// Update user attributes
export const updateUserAttributes = async (
  accessToken: string,
  attributes: AttributeType[]
) => {
  const params = {
    AccessToken: accessToken,
    UserAttributes: attributes
  };

  try {
    const command = new UpdateUserAttributesCommand(params);
    const response = await cognitoClient.send(command);
    return response;
  } catch (error) {
    console.error("Error updating user attributes:", error);
    throw error;
  }
};

// Respond to authentication challenge
export const respondToAuthChallenge = async (
  username: string, 
  newPassword: string, 
  session: string,
  userAttributes?: Record<string, string>
) => {
  const challengeResponses: Record<string, string> = {
    USERNAME: username,
    NEW_PASSWORD: newPassword,
  };

  // Add user attributes if provided (required for NEW_PASSWORD_REQUIRED challenge)
  if (userAttributes) {
    Object.entries(userAttributes).forEach(([key, value]) => {
      challengeResponses[`userAttributes.${key}`] = value;
    });
  }

  const params = {
    ChallengeName: ChallengeNameType.NEW_PASSWORD_REQUIRED,
    ClientId: clientId,
    ChallengeResponses: challengeResponses,
    Session: session
  };

  try {
    const command = new RespondToAuthChallengeCommand(params);
    const response = await cognitoClient.send(command);
    return response;
  } catch (error) {
    console.error("Error responding to auth challenge:", error);
    throw error;
  }
};
